import { useState } from 'react';
import { Check, ChevronDown, ChevronRight, FileText, Sparkles, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useApproveDceClassification,
  useDceUploadDocuments,
  useDceUploads,
  useDeleteDceUpload,
  useReclassifyDceDocument,
} from '../../hooks';
import {
  DCE_DOCUMENT_TYPE_LABELS,
  DCE_DOCUMENT_TYPES,
  DCE_UPLOAD_STATUS_LABELS,
  type DceClassifiedBy,
  type DceDocumentType,
  type DceUpload,
  type DceUploadDocument,
  type DceUploadStatus,
} from '../../types/dce-upload.types';

interface DceUploadsListProps {
  opportunityId: number;
}

const STATUS_VARIANT: Record<
  DceUploadStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  PENDING: 'outline',
  UNZIPPING: 'secondary',
  CLASSIFYING: 'secondary',
  EXTRACTING: 'secondary',
  INDEXING: 'secondary',
  DONE: 'default',
  PARTIAL_SUCCESS: 'default',
  FAILED: 'destructive',
};

const CLASSIFIED_BY_LABEL: Record<DceClassifiedBy, string> = {
  RULE: 'Règle',
  AI: 'IA',
  MANUAL: 'Manuel',
};

function formatBytes(input: string | number | null): string {
  const bytes = typeof input === 'string' ? Number(input) : input ?? 0;
  if (!Number.isFinite(bytes)) return '–';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
}

function confidenceTone(conf: number | null): string {
  if (conf === null) return 'text-muted-foreground';
  if (conf >= 0.9) return 'text-emerald-600';
  if (conf >= 0.7) return 'text-amber-600';
  return 'text-red-600';
}

export function DceUploadsList({ opportunityId }: DceUploadsListProps) {
  const { data: uploads, isLoading } = useDceUploads(opportunityId);
  const deleteMutation = useDeleteDceUpload(opportunityId);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Imports DCE</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Chargement…</p>
        </CardContent>
      </Card>
    );
  }

  if (!uploads || uploads.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Imports DCE ({uploads.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {uploads.map((upload) => (
          <DceUploadRow
            key={upload.id}
            opportunityId={opportunityId}
            upload={upload}
            expanded={expandedId === upload.id}
            onToggle={() =>
              setExpandedId(expandedId === upload.id ? null : upload.id)
            }
            onDelete={() => {
              if (
                window.confirm(
                  'Supprimer cet import DCE ? Les fichiers seront purgés sous 30 jours.',
                )
              ) {
                deleteMutation.mutate(upload.id);
              }
            }}
            deletePending={deleteMutation.isPending}
          />
        ))}
      </CardContent>
    </Card>
  );
}

interface DceUploadRowProps {
  opportunityId: number;
  upload: DceUpload;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  deletePending: boolean;
}

function DceUploadRow({
  opportunityId,
  upload,
  expanded,
  onToggle,
  onDelete,
  deletePending,
}: DceUploadRowProps) {
  const approveMutation = useApproveDceClassification(opportunityId, upload.id);
  const canApprove =
    (upload.status === 'DONE' || upload.status === 'PARTIAL_SUCCESS') &&
    !upload.classificationApprovedAt;

  return (
    <div className="border-b pb-3 last:border-b-0 last:pb-0">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-start gap-2 text-left hover:opacity-80 transition-opacity min-w-0"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
          )}
          <div className="flex-1 space-y-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={STATUS_VARIANT[upload.status]}>
                {DCE_UPLOAD_STATUS_LABELS[upload.status]}
              </Badge>
              {upload.classificationApprovedAt && (
                <Badge variant="outline" className="border-emerald-500 text-emerald-700">
                  <Check className="h-3 w-3 mr-1" />
                  Validé
                </Badge>
              )}
              <span className="text-sm font-medium truncate">
                {upload.sourceType === 'ZIP'
                  ? upload.sourceFilename ?? 'Archive ZIP'
                  : `${upload.fileCount} fichier${upload.fileCount > 1 ? 's' : ''}`}
              </span>
              <span className="text-xs text-muted-foreground">
                · {formatBytes(upload.totalSizeBytes)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Importé le {new Date(upload.uploadedAt).toLocaleString('fr-FR')}
              {upload.fileCount > 0 && (
                <span>
                  {' '}
                  · {upload.fileCount} document
                  {upload.fileCount > 1 ? 's' : ''}
                </span>
              )}
              {upload.failedCount > 0 && (
                <span className="text-amber-600">
                  {' '}
                  · {upload.failedCount} en erreur
                </span>
              )}
            </p>
            {upload.errorMessage && (
              <p className="text-xs text-red-600">{upload.errorMessage}</p>
            )}
          </div>
        </button>
        <div className="flex items-center gap-1">
          {canApprove && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => approveMutation.mutate()}
              disabled={approveMutation.isPending}
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Valider classification
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            disabled={deletePending}
          >
            <Trash2 className="h-4 w-4 text-red-600" />
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 ml-6">
          <DceUploadDocumentsList uploadId={upload.id} />
        </div>
      )}
    </div>
  );
}

function DceUploadDocumentsList({ uploadId }: { uploadId: number }) {
  const { data, isLoading } = useDceUploadDocuments(uploadId);
  const reclassify = useReclassifyDceDocument(uploadId);

  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Chargement des fichiers…</p>;
  }
  if (!data || data.length === 0) {
    return <p className="text-xs text-muted-foreground">Aucun fichier.</p>;
  }

  return (
    <ul className="space-y-1">
      {data.map((doc) => (
        <DceDocumentRow
          key={doc.id}
          doc={doc}
          onChangeType={(newType) =>
            reclassify.mutate({ documentId: doc.id, dceDocumentType: newType })
          }
          changing={reclassify.isPending && reclassify.variables?.documentId === doc.id}
        />
      ))}
    </ul>
  );
}

interface DceDocumentRowProps {
  doc: DceUploadDocument;
  onChangeType: (type: DceDocumentType) => void;
  changing: boolean;
}

function DceDocumentRow({ doc, onChangeType, changing }: DceDocumentRowProps) {
  const conf = doc.dceClassificationConf;
  return (
    <li className="flex items-center gap-2 text-sm py-1.5 px-2 rounded hover:bg-muted/50">
      <FileText className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      <span
        className="flex-1 truncate min-w-0"
        title={doc.dceRelativePath ?? doc.filename}
      >
        {doc.dceRelativePath ?? doc.filename}
      </span>
      {doc.dceClassifiedBy && (
        <span
          className={`text-[10px] uppercase tracking-wide ${
            doc.dceClassifiedBy === 'AI' ? 'text-violet-600' : 'text-muted-foreground'
          }`}
          title={`Classifié par ${CLASSIFIED_BY_LABEL[doc.dceClassifiedBy]}${
            conf !== null ? ` (confiance ${Math.round(conf * 100)}%)` : ''
          }`}
        >
          {doc.dceClassifiedBy === 'AI' && <Sparkles className="h-3 w-3 inline mr-0.5" />}
          {CLASSIFIED_BY_LABEL[doc.dceClassifiedBy]}
        </span>
      )}
      {conf !== null && (
        <span className={`text-[10px] tabular-nums ${confidenceTone(conf)}`}>
          {Math.round(conf * 100)}%
        </span>
      )}
      <Select
        value={doc.dceDocumentType ?? undefined}
        onValueChange={(value) => onChangeType(value as DceDocumentType)}
        disabled={changing}
      >
        <SelectTrigger className="h-7 w-[160px] text-xs">
          <SelectValue placeholder="Non classé" />
        </SelectTrigger>
        <SelectContent>
          {DCE_DOCUMENT_TYPES.map((type) => (
            <SelectItem key={type} value={type} className="text-xs">
              {DCE_DOCUMENT_TYPE_LABELS[type]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-xs text-muted-foreground flex-shrink-0 w-16 text-right tabular-nums">
        {formatBytes(doc.fileSize)}
      </span>
    </li>
  );
}
